<template>
  <div class="notification-center-wrapper">
    <!-- Bell Icon Button -->
    <button
      ref="bellButton"
      class="notification-center-bell-btn"
      :title="t('notificationCenter.title')"
      @click.stop="toggleDropdown"
    >
      <i class="ri-notification-3-line text-xl text-[#6E6E6E]"></i>
      <span
        v-if="totalUnreadCount > 0"
        class="notification-center-badge"
      >
        {{ totalUnreadCount > 99 ? '99+' : totalUnreadCount }}
      </span>
    </button>

    <!-- Dropdown using Teleport to render at body level -->
    <Teleport to="body">
      <div
        v-show="isOpen"
        ref="dropdown"
        class="notification-center-dropdown-portal"
        :style="dropdownStyle"
        @click.stop
      >
        <!-- 
          Header - New design with actions (when new Notification Center experience is active)
          Only shows when enableNotificationCenter=true AND enableWeatherAlerts=false
        -->
        <div v-if="showNewNotificationCenter" class="notification-center-header">
          <span class="header-title">{{ t('notificationCenter.title') }}</span>
          <div class="header-actions">
            <button 
              class="header-action-btn"
              :class="{ 'disabled': totalUnreadCount === 0 }"
              :disabled="totalUnreadCount === 0"
              @click="markAllAsRead"
            >
              {{ t('notificationCenter.markAllAsRead') }}
            </button>
            <a href="/notification-center" class="header-action-link">{{ t('notificationCenter.viewAll') }}</a>
            <button class="header-settings-btn" @click="openSettings">
              <i class="ri-settings-3-line"></i>
            </button>
          </div>
        </div>

        <!-- 
          Header - Legacy simple design (weather alerts legacy behavior)
          Shows when enableWeatherAlerts=true OR enableNotificationCenter=false
        -->
        <div v-else class="notification-center-header notification-center-header-legacy">
          {{ t('weatherAlerts.notificationsTitle') }}
        </div>

        <div class="notification-center-body">
          <!-- Loading State -->
          <div v-if="loading" class="notification-center-loading">
            <div class="spinner"></div>
          </div>

          <!-- Empty State -->
          <div v-else-if="allNotifications.length === 0" class="notification-center-empty">
            <div class="notification-center-empty-content">

              <!-- 
                New empty state experience (when new Notification Center experience is active)
              -->
              <template v-if="showNewNotificationCenter">
                <!-- No notifications configured - show setup message -->
                <template v-if="!hasWeatherAlertsConfigured">
                  <p class="empty-state-title">{{ t('notificationCenter.noNotificationsAllowed') }}</p>
                  <p class="empty-state-message">{{ t('notificationCenter.noNotificationsEnabledMessage') }}</p>
                  <a href="/notification-center" class="notification-center-configure-link">
                    {{ t('notificationCenter.setupNotifications') }}
                  </a>
                </template>

                <!-- Notifications configured but none available -->
                <template v-else>
                  <p class="empty-state-title">{{ t('notificationCenter.noNotificationsTitle') }}</p>
                  <p class="empty-state-message">{{ t('notificationCenter.noNotificationsMessage') }}</p>
                </template>
              </template>

              <!-- 
                Legacy empty state (weather alerts legacy behavior)
              -->
              <template v-else>
                <p><strong>{{ t('notificationCenter.noNotifications') }}</strong></p>
                <a v-if="enableWeatherAlerts && !hasWeatherAlertsConfigured" href="/weather-settings" class="notification-center-configure-link">
                  {{ t('weatherAlerts.configureAlerts') }}
                </a>
              </template>
            </div>
          </div>

          <!-- Notifications List -->
          <div v-else class="notification-center-items">
            <div
              v-for="notification in allNotifications"
              :key="getNotificationKey(notification)"
              class="notification-item"
              :class="{ 'unread': !isNotificationDismissed(notification) }"
            >
              <!-- Icon -->
              <div class="notification-icon-wrapper">
                <i :class="getNotificationIcon(notification)"></i>
              </div>

              <!-- Content -->
              <div class="notification-content">
                <!-- Title (Order Placed, Order En Route, etc.) -->
                <div class="notification-title">
                  {{ getNotificationTitle(notification) }}
                </div>

                <!-- Message -->
                <div class="notification-message">
                  {{ getNotificationMessage(notification) }}
                </div>

                <!-- Footer: Timestamp and Actions -->
                <div class="notification-footer">
                  <span class="notification-time">
                    {{ getNotificationTimestamp(notification) }}
                  </span>
                  <div class="notification-actions">
                    <!-- Primary Action: View Order -->
                    <a
                      v-if="!enableNotificationCenter || isOrderNotification(notification)"
                      href="javascript:void(0)"
                      class="notification-link"
                      @click.stop="handleViewOrder(notification)"
                    >
                      {{ t('notificationCenter.viewOrder') }}
                    </a>
                    <!-- Secondary Action: Leave Delivery Feedback (only for Delivered status) -->
                    <a
                      v-if="isDeliveredStatus(notification)"
                      href="javascript:void(0)"
                      class="notification-link secondary"
                      @click.stop="handleLeaveDeliveryFeedback(notification)"
                    >
                      {{ t('notificationCenter.leaveDeliveryFeedback') }}
                    </a>
                    <!-- Weather Alert Action (Notification Center experience) -->
                    <a
                      v-if="enableNotificationCenter && isWeatherNotification(notification)"
                      href="javascript:void(0)"
                      class="notification-link"
                      @click.stop="handleViewWeatherReport(notification)"
                    >
                      {{ t('weatherAlerts.viewFullReport') }}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, inject, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { getLanguage } from '@/utilities/cookies';
import weatherAlertsService from '@/services/weatherAlertsService';
import orderNotificationsService from '@/services/orderNotificationsService';
import notificationCenterService from '@/services/notificationCenterService';
import {
  NOTIFICATION_TYPE,
  ORDER_STATUS,
  SERVER_NOTIFICATION_TYPE,
  DELIVERED_MESSAGE_KEYS
} from '@/utilities/notificationCenterConstants';

const { t, locale } = useI18n();
locale.value = getLanguage();

// Inject config from parent
const config = inject('config');

// Global Notification Center feature flag - controls the entire new Notification Center experience
const enableNotificationCenter = computed(() => config?.value?.enableNotificationCenter ?? false);

// Individual notification type flags - control visibility of their respective notification types
const enableWeatherAlerts = computed(() => config?.value?.enableWeatherAlertsFeature ?? false);
const enableOrderStatusNotifications = computed(() => config?.value?.showOrderStatusNotification ?? false);

// The new Notification Center experience is driven solely by its own flag now
const showNewNotificationCenter = computed(() => enableNotificationCenter.value);

// Refs
const bellButton = ref(null);
const dropdown = ref(null);

// State
const isOpen = ref(false);
const loading = ref(false);
const weatherNotifications = ref([]);
const orderNotifications = ref([]);
const weatherUnreadCount = ref(0);
const orderUnreadCount = ref(0);
const hasWeatherAlertsConfigured = ref(true);
const isDataLoaded = ref(false);
const dropdownStyle = ref({});

// Unified Notification Center endpoint state (used when enableNotificationCenter is on)
const NC_MAX_ITEMS = 20;
const ncNotifications = ref([]);
const ncUnreadCount = ref(0);

// Mock data for order notifications based on Story 2 requirements
const mockOrderNotifications = [
  {
    id: 1,
    notificationId: 'notif-001',
    orderNumber: '123456',
    status: ORDER_STATUS.PLACED,
    title: 'Order Placed',
    message: 'Your order #123456 has been successfully placed and is being processed.',
    timestamp: new Date(Date.now() - 1000 * 60 * 2).toISOString(), // 2 minutes ago
    relativeTimestamp: 'Just Now',
    isRead: false,
    _notificationType: NOTIFICATION_TYPE.ORDER
  },
  {
    id: 2,
    notificationId: 'notif-002',
    orderNumber: '123456',
    status: ORDER_STATUS.EN_ROUTE,
    title: 'Order En Route',
    message: 'Your order #123456 is on its way to the delivery location.',
    timestamp: new Date(Date.now() - 1000 * 60 * 30).toISOString(), // 30 minutes ago
    relativeTimestamp: 'Just Now',
    isRead: false,
    _notificationType: NOTIFICATION_TYPE.ORDER
  },
  {
    id: 3,
    notificationId: 'notif-003',
    orderNumber: '123457',
    status: ORDER_STATUS.ARRIVED,
    title: 'Order Arrived',
    message: 'Your order #123457 has arrived at its destination.',
    timestamp: new Date(Date.now() - 1000 * 60 * 60).toISOString(), // 1 hour ago
    relativeTimestamp: 'Just Now',
    isRead: false,
    _notificationType: NOTIFICATION_TYPE.ORDER
  },
  {
    id: 4,
    notificationId: 'notif-004',
    orderNumber: '123458',
    status: ORDER_STATUS.DELIVERED,
    title: 'Order Delivered',
    message: 'Your order #123458 has been delivered successfully.',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString(), // 2 hours ago
    relativeTimestamp: 'Just Now',
    isRead: true,
    _notificationType: NOTIFICATION_TYPE.ORDER
  }
];

// Parse the stringified MetadataJson payload into an object
const parseMetadata = (raw) => {
  if (!raw) return {};
  if (typeof raw === 'object') return raw;
  try {
    return JSON.parse(raw);
  } catch (error) {
    console.error('Failed to parse notification metadata:', error);
    return {};
  }
};

// Maps the canonical status stored in MetadataJson to the component's ORDER_STATUS values
const SERVER_STATUS_MAP = {
  'placed': ORDER_STATUS.PLACED,
  'en route': ORDER_STATUS.EN_ROUTE,
  'arrived': ORDER_STATUS.ARRIVED,
  'delivered': ORDER_STATUS.DELIVERED
};

const formatTimestamp = (isoDate) => {
  if (!isoDate) return '';
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString(getLanguage(), { dateStyle: 'medium', timeStyle: 'short' });
};

// Map a server notification into the shape the display helpers already expect
const mapServerNotification = (n) => {
  const metadata = parseMetadata(n.metadataJson ?? n.MetadataJson);
  const type = (n.notificationType ?? n.NotificationType) === SERVER_NOTIFICATION_TYPE.ORDER
    ? NOTIFICATION_TYPE.ORDER
    : NOTIFICATION_TYPE.WEATHER;
  const id = n.notificationCenterId ?? n.NotificationCenterId;
  const serverRead = !!(n.isRead ?? n.IsRead);
  const rawStatus = (metadata.status ?? '').toString().toLowerCase();
  return {
    ...n,
    id,
    _notificationType: type,
    _metadata: metadata,
    status: SERVER_STATUS_MAP[rawStatus] ?? rawStatus,
    orderNumber: metadata.orderNumber ?? n.relatedEntityId ?? n.RelatedEntityId ?? '',
    relativeTimestamp: formatTimestamp(n.createdDateUtc ?? n.CreatedDateUtc),
    isRead: serverRead
  };
};

// Computed
const totalUnreadCount = computed(() => {
  if (enableNotificationCenter.value) {
    return ncUnreadCount.value;
  }
  let count = 0;
  if (enableWeatherAlerts.value) {
    count += weatherUnreadCount.value;
  }
  if (enableOrderStatusNotifications.value) {
    count += orderUnreadCount.value;
  }
  return count;
});

// Check if any notification type is enabled (feature flags)
// This determines if the user is ALLOWED to see notifications
const hasAnyNotificationsEnabled = computed(() => {
  return enableWeatherAlerts.value || enableOrderStatusNotifications.value;
});

const allNotifications = computed(() => {
  if (enableNotificationCenter.value) {
    return ncNotifications.value;
  }

  const notifications = [];

  // Add weather notifications if enabled
  if (enableWeatherAlerts.value && weatherNotifications.value) {
    weatherNotifications.value.forEach(n => {
      notifications.push({
        ...n,
        _notificationType: NOTIFICATION_TYPE.WEATHER
      });
    });
  }

  // Add order notifications if enabled
  if (enableOrderStatusNotifications.value && orderNotifications.value) {
    orderNotifications.value.forEach(n => {
      notifications.push({
        ...n,
        _notificationType: n._notificationType || NOTIFICATION_TYPE.ORDER
      });
    });
  }

  // Sort by timestamp (most recent first)
  return notifications.sort((a, b) => {
    const dateA = getNotificationDate(a);
    const dateB = getNotificationDate(b);
    return dateB - dateA;
  });
});

// Helper functions
const getNotificationKey = (n) => {
  const type = n._notificationType || NOTIFICATION_TYPE.WEATHER;
  if (type === NOTIFICATION_TYPE.WEATHER) {
    return `weather-${n.WeatherAlertsHistoryId || n.weatherAlertsHistoryId || n.id}`;
  }
  return `order-${n.notificationId || n.NotificationId || n.id}`;
};

const getNotificationType = (n) => n._notificationType || NOTIFICATION_TYPE.WEATHER;

const isOrderNotification = (n) => getNotificationType(n) === NOTIFICATION_TYPE.ORDER;
const isWeatherNotification = (n) => getNotificationType(n) === NOTIFICATION_TYPE.WEATHER;

const getNotificationTitle = (n) => {
  const type = getNotificationType(n);
  if (type === NOTIFICATION_TYPE.ORDER) {
    // Use the title from the notification or generate based on status
    if (n.title || n.Title) {
      return n.title || n.Title;
    }
    const status = (n.status || n.Status || '').toLowerCase();
    switch (status) {
      case ORDER_STATUS.PLACED:
        return t('notificationCenter.orderPlaced');
      case ORDER_STATUS.EN_ROUTE:
        return t('notificationCenter.orderEnRoute');
      case ORDER_STATUS.ARRIVED:
        return t('notificationCenter.orderArrived');
      case ORDER_STATUS.DELIVERED:
        return t('notificationCenter.orderDelivered');
      default:
        return t('notificationCenter.orderUpdate');
    }
  }
  return n.Title || n.title || '';
};

const getNotificationMessage = (n) => {
  const type = getNotificationType(n);
  if (type === NOTIFICATION_TYPE.ORDER) {
    // Use the message from the notification or generate based on status
    if (n.message || n.Message) {
      return n.message || n.Message;
    }
    const orderNumber = n.orderNumber || n.OrderNumber || '';
    const status = (n.status || n.Status || '').toLowerCase();
    switch (status) {
      case ORDER_STATUS.PLACED:
        return t('notificationCenter.orderPlacedMessage', { orderNumber });
      case ORDER_STATUS.EN_ROUTE:
        return t('notificationCenter.orderEnRouteMessage', { orderNumber });
      case ORDER_STATUS.ARRIVED:
        return t('notificationCenter.orderArrivedMessage', { orderNumber });
      case ORDER_STATUS.DELIVERED:
        return t('notificationCenter.orderDeliveredMessage', { orderNumber });
      default:
        return n.StatusMessage || n.statusMessage || '';
    }
  }
  // Weather notification details (server may send a pre-rendered message)
  const details = n.Details || n.details || '';
  const headline = n.Headline || n.headline || '';
  return n.message || n.Message || details || headline;
};

const getNotificationTimestamp = (n) => n.relativeTimestamp || n.RelativeTimestamp || n.Timestamp || n.timestamp || '';

const getNotificationDate = (n) => {
  const dateStr = n.timestamp || n.Timestamp || n.DateTime || n.dateTime || n.CreatedAt || n.createdAt;
  return dateStr ? new Date(dateStr) : new Date(0);
};

const isNotificationDismissed = (n) => !!(n.isRead || n.IsRead || n.Dismissed || n.dismissed);

const isDeliveredStatus = (n) => {
  const type = getNotificationType(n);
  if (type !== NOTIFICATION_TYPE.ORDER) return false;
  const status = (n.status || n.Status || '').toLowerCase();
  if (status === ORDER_STATUS.DELIVERED) return true;
  const messageKey = (n.messageKey || n.MessageKey || '').toLowerCase();
  return DELIVERED_MESSAGE_KEYS.includes(messageKey);
};

const getNotificationIcon = (n) => {
  const type = getNotificationType(n);
  if (type === NOTIFICATION_TYPE.ORDER) {
    const status = (n.status || n.Status || '').toLowerCase();
    // Cart icon for placed orders, truck for all others
    if (status === ORDER_STATUS.PLACED) {
      return 'ri-shopping-cart-fill notification-icon order-icon';
    }
    return 'ri-truck-fill notification-icon order-icon';
  }
  // Weather icon
  return 'ri-cloud-fill notification-icon weather-icon';
};

// Methods
const updateDropdownPosition = () => {
  if (!bellButton.value) return;

  const buttonRect = bellButton.value.getBoundingClientRect();
  const isMobile = window.innerWidth <= 768;

  if (isMobile) {
    dropdownStyle.value = {
      position: 'fixed',
      top: '60px',
      right: '10px',
      left: '10px',
      width: 'auto',
      maxWidth: 'none'
    };
  } else {
    dropdownStyle.value = {
      position: 'fixed',
      top: `${buttonRect.bottom + 8}px`,
      right: `${window.innerWidth - buttonRect.right}px`,
      width: '450px',
      maxWidth: 'calc(100vw - 20px)'
    };
  }
};

const toggleDropdown = async () => {
  isOpen.value = !isOpen.value;

  if (isOpen.value) {
    await nextTick();
    updateDropdownPosition();

    if (!isDataLoaded.value) {
      await loadAllNotifications();
    }
  }
};

const closeDropdown = () => {
  isOpen.value = false;
};

const loadAllNotifications = async () => {
  loading.value = true;

  try {
    if (enableNotificationCenter.value) {
      await loadFromNotificationCenter();
    } else {
      const promises = [];
      if (enableWeatherAlerts.value) {
        promises.push(loadWeatherNotifications());
      }
      if (enableOrderStatusNotifications.value) {
        promises.push(loadOrderNotifications());
      }
      await Promise.all(promises);
    }
    isDataLoaded.value = true;
  } catch (error) {
    console.error('Error loading notifications:', error);
  } finally {
    loading.value = false;
  }
};

const loadFromNotificationCenter = async () => {
  const response = await notificationCenterService.getNotifications({
    daysBack: 20,
    skip: 0,
    take: NC_MAX_ITEMS,
    includeDismissed: false,
    sortByMostRecent: true
  });

  if (response.success) {
    const core = response.data?.data ?? response.data?.Data ?? response.data;
    ncNotifications.value = (core?.notifications ?? core?.Notifications ?? []).map(mapServerNotification);
    ncUnreadCount.value = core?.unreadCount ?? core?.UnreadCount ?? 0;
  } else {
    ncNotifications.value = [];
    ncUnreadCount.value = 0;
  }
};

const loadWeatherNotifications = async () => {
  try {
    const response = await weatherAlertsService.getTriggeredAlerts();

    if (response.success) {
      const data = response.data?.data || response.data?.Data || response.data;
      const core = data?.data || data?.Data || data;

      weatherNotifications.value = core?.Notifications || core?.notifications || [];
      weatherUnreadCount.value = core?.UnreadCount ?? core?.unreadCount ?? 0;
      hasWeatherAlertsConfigured.value = core?.AnyAlertsConfigured ?? core?.anyAlertsConfigured ?? false;
    } else {
      weatherNotifications.value = [];
      weatherUnreadCount.value = 0;
      hasWeatherAlertsConfigured.value = false;
    }
  } catch (error) {
    console.error('Error loading weather notifications:', error);
    weatherNotifications.value = [];
    weatherUnreadCount.value = 0;
    hasWeatherAlertsConfigured.value = false;
  }
};

const loadOrderNotifications = async () => {
  // TODO: Replace mock data with API call when backend is ready
  orderNotifications.value = mockOrderNotifications.map(n => ({ ...n }));
  orderUnreadCount.value = orderNotifications.value.filter(n => !n.isRead).length;

  // Backend API call (uncomment when ready)
  /*
  try {
    const response = await orderNotificationsService.getOrderNotifications();

    if (response.success) {
      const data = response.data?.data || response.data?.Data || response.data;
      const core = data?.data || data?.Data || data;

          orderNotifications.value = core?.Notifications || core?.notifications || [];
            orderUnreadCount.value = core?.UnreadCount ?? core?.unreadCount ?? 0;
          } else {
            orderNotifications.value = [];
            orderUnreadCount.value = 0;
          }
        } catch (error) {
          console.error('Error loading order notifications:', error);
          orderNotifications.value = [];
          orderUnreadCount.value = 0;
        }
        */
      };

const markAllAsRead = async () => {
  if (totalUnreadCount.value === 0) return;

  if (enableNotificationCenter.value) {
    const unread = ncNotifications.value.filter(n => n.id != null && !n.isRead);
    ncNotifications.value = ncNotifications.value.map(n => ({ ...n, isRead: true }));
    ncUnreadCount.value = 0;
    await Promise.all(unread.map(n =>
      notificationCenterService.markAsRead(n.id, n.notificationType || n.NotificationType || null)));
    return;
  }

  // Mark all order notifications as read (mock data)
  orderNotifications.value = orderNotifications.value.map(n => ({
    ...n,
    isRead: true
  }));
  orderUnreadCount.value = 0;

  // TODO: Uncomment when backend is ready
  /*
  try {
    await orderNotificationsService.markAllAsRead();
    await loadOrderNotifications();
  } catch (error) {
    console.error('Error marking all as read:', error);
  }
  */
};

const openSettings = () => {
  closeDropdown();
  // Navigate to notification settings - could be weather settings or a general notification settings page
  window.location.href = '/weather-settings';
};

// Mark a Notification Center item as read (persists to backend)
const markNcNotificationRead = (notification) => {
  const id = notification.id;
  if (id == null) return;
  const index = ncNotifications.value.findIndex(n => n.id === id);
  if (index !== -1 && !ncNotifications.value[index].isRead) {
    ncNotifications.value[index] = { ...ncNotifications.value[index], isRead: true };
    ncUnreadCount.value = Math.max(0, ncUnreadCount.value - 1);
    const notificationType = notification.notificationType || notification.NotificationType || null;
    notificationCenterService.markAsRead(id, notificationType);
  }
};

// Navigate using the server-provided actionLink when present, otherwise fall back to the order deep link
const navigateFromActionLink = (notification, feedback = false) => {
  const actionLink = notification.actionLink || notification.ActionLink;
  if (actionLink) {
    const separator = actionLink.includes('?') ? '&' : '?';
    window.location.href = feedback ? `${actionLink}${separator}feedback=true` : actionLink;
    return;
  }
  const orderNumber = notification.orderNumber || notification.OrderNumber || '';
  window.location.href = feedback ? `/orders#${orderNumber}&feedback=true` : `/orders#${orderNumber}`;
};

const handleViewWeatherReport = (notification) => {
  markNcNotificationRead(notification);
  closeDropdown();
  const actionLink = notification.actionLink || notification.ActionLink;
  if (actionLink) {
    window.location.href = actionLink;
    return;
  }
  const historyId = notification.WeatherAlertsHistoryId || notification.weatherAlertsHistoryId
    || notification.relatedEntityId || notification.RelatedEntityId || notification.id;
  window.location.href = `/weather-reports?historyId=${historyId}`;
};

const handleViewOrder = async (notification) => {
  if (enableNotificationCenter.value) {
    markNcNotificationRead(notification);
    closeDropdown();
    navigateFromActionLink(notification);
    return;
  }

  const orderNumber = notification.orderNumber || notification.OrderNumber;
  closeDropdown();

  // Mark as read if not already (mock data)
  if (!isNotificationDismissed(notification)) {
    const index = orderNotifications.value.findIndex(n => n.id === notification.id);
    if (index !== -1) {
      orderNotifications.value[index].isRead = true;
      orderUnreadCount.value = Math.max(0, orderUnreadCount.value - 1);
    }
    // TODO: Uncomment when backend is ready
    /*
    try {
      const notificationId = notification.notificationId || notification.NotificationId || notification.id;
      await orderNotificationsService.dismissOrderNotification(notificationId);
    } catch (error) {
      console.error('Error dismissing notification:', error);
    }
    */
  }

  // Redirect to order details page
  window.location.href = `/orders#${orderNumber}`;
};

const handleLeaveDeliveryFeedback = async (notification) => {
  if (enableNotificationCenter.value) {
    markNcNotificationRead(notification);
    closeDropdown();
    navigateFromActionLink(notification, true);
    return;
  }

  const orderNumber = notification.orderNumber || notification.OrderNumber;
  closeDropdown();

  // Mark as read if not already (mock data)
  if (!isNotificationDismissed(notification)) {
    const index = orderNotifications.value.findIndex(n => n.id === notification.id);
    if (index !== -1) {
      orderNotifications.value[index].isRead = true;
      orderUnreadCount.value = Math.max(0, orderUnreadCount.value - 1);
    }
    // TODO: Uncomment when backend is ready
    /*
    try {
      const notificationId = notification.notificationId || notification.NotificationId || notification.id;
      await orderNotificationsService.dismissOrderNotification(notificationId);
    } catch (error) {
      console.error('Error dismissing notification:', error);
    }
    */
  }

  // Redirect to orders page with feedback modal trigger
  window.location.href = `/orders#${orderNumber}&feedback=true`;
};

// Event handlers
const handleKeydown = (e) => {
  if (e.key === 'Escape' && isOpen.value) {
    closeDropdown();
  }
};

const handleClickOutside = (e) => {
  if (!isOpen.value) return;

  const clickedDropdown = dropdown.value && dropdown.value.contains(e.target);
  const clickedBell = bellButton.value && bellButton.value.contains(e.target);

  if (!clickedDropdown && !clickedBell) {
    closeDropdown();
  }
};

const handleResize = () => {
  if (isOpen.value) {
    updateDropdownPosition();
  }
};

const handleScroll = () => {
  if (isOpen.value) {
    updateDropdownPosition();
  }
};

// Lifecycle
onMounted(() => {
  // Load notifications on mount if any notification feature is enabled
  if (enableNotificationCenter.value || enableWeatherAlerts.value || enableOrderStatusNotifications.value) {
    loadAllNotifications();
  }

  document.addEventListener('keydown', handleKeydown);
  document.addEventListener('click', handleClickOutside);
  window.addEventListener('resize', handleResize);
  window.addEventListener('scroll', handleScroll, true);
});

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown);
  document.removeEventListener('click', handleClickOutside);
  window.removeEventListener('resize', handleResize);
  window.removeEventListener('scroll', handleScroll, true);
});
</script>

<style scoped>
.notification-center-wrapper {
  display: flex;
  align-items: center;
}

.notification-center-bell-btn {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.notification-center-bell-btn:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.notification-center-bell-btn:focus {
  outline: none;
  box-shadow: 0 0 0 2px rgba(118, 33, 35, 0.2);
}

.notification-center-bell-btn:active {
  background-color: rgba(0, 0, 0, 0.1);
}

.notification-center-badge {
  position: absolute;
  top: 4px;
  right: 4px;
  background-color: #f1a600;
  color: #000;
  font-weight: bold;
  font-size: 9px;
  min-width: 16px;
  height: 16px;
  line-height: 16px;
  text-align: center;
  border-radius: 50%;
  padding: 0 4px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.notification-center-dropdown-portal {
  background: #fff;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border: 1px solid #d1d1d1;
  z-index: 1050;
}

.notification-center-header {
  padding: 12px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #f0f0f0;
}

/* Legacy header style - simple title only */
.notification-center-header-legacy {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

.header-title {
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-action-btn {
  background: none;
  border: none;
  color: #6c757d;
  font-size: 12px;
  cursor: pointer;
  padding: 0;
}

.header-action-btn:hover:not(.disabled) {
  color: #762123;
  text-decoration: underline;
}

.header-action-btn.disabled {
  color: #ccc;
  cursor: not-allowed;
}

.header-action-link {
  color: #6c757d;
  font-size: 12px;
  text-decoration: none;
}

.header-action-link:hover {
  color: #762123;
  text-decoration: underline;
}

.header-settings-btn {
  background: none;
  border: none;
  color: #6c757d;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-settings-btn:hover {
  color: #762123;
}

.header-settings-btn i {
  font-size: 16px;
}

.notification-center-body {
  max-height: 400px;
  overflow-y: auto;
  margin-bottom: 16px;
}

.notification-center-loading {
  text-align: center;
  padding: 40px 20px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #762123;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.notification-center-empty {
  text-align: center;
  padding: 10px 20px;
  color: #6c757d;
}

.notification-center-empty-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.notification-center-empty-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background-color: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
}

.notification-center-empty-icon i {
  font-size: 24px;
  color: #999;
}

.empty-state-title {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.empty-state-message {
  font-size: 13px;
  color: #6c757d;
  margin: 0;
}

/* Legacy empty state styling */
.notification-center-empty-content p {
  margin-bottom: 8px;
}

.notification-center-empty-icon i {
  font-size: 24px;
  color: #999;
}

.empty-state-title {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.empty-state-message {
  font-size: 13px;
  color: #6c757d;
  margin: 0;
}

.notification-center-configure-link {
  color: #762123;
  text-decoration: underline;
  cursor: pointer;
  font-size: 13px;
  margin-top: 4px;
}

.notification-center-configure-link:hover {
  color: #5a1a1c;
}

.notification-center-items {
  padding: 0 0 16px;
}

.notification-item {
  padding: 12px 16px 12px 46px;
  border-bottom: 1px solid #d1d1d1;
  position: relative;
  background: #fff;
  transition: background-color 0.2s;
}

.notification-item:last-child {
  border-bottom: none;
}

.notification-item:hover {
  background-color: #f8f9fa;
}

.notification-item.unread {
  background-color: #fef9e6;
}

.notification-item.unread:hover {
  background-color: #fdf3d0;
}

.notification-icon-wrapper {
  position: absolute;
  left: 0;
  top: -5px;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: flex-start;
  justify-content: center;
}

.notification-icon {
  font-size: 20px;
  color: #762123;
  left: 16px !important;
}

.notification-icon.order-icon {
  color: #762123;
}

.notification-icon.weather-icon {
  color: #762123;
}

.notification-content {
  flex: 1;
}

.notification-title {
  font-weight: 600;
  font-size: 13px;
  color: #8b3a3a;
  margin-bottom: 4px;
}

.notification-message {
  color: #171717;
  font-size: 12px;
  line-height: 1.4;
  margin-bottom: 8px;
}

.notification-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.notification-time {
  color: #762123;
  font-size: 11px;
}

.notification-actions {
  display: flex;
  gap: 12px;
}

.notification-link {
  color: #762123;
  text-decoration: underline;
  font-size: 12px;
  cursor: pointer;
  transition: color 0.2s;
}

.notification-link:hover {
  color: #5a1a1c;
}

.notification-link.secondary {
  color: #666;
}

.notification-link.secondary:hover {
  color: #762123;
}
</style>
